import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  GUEST_DEMO_LIMIT,
  SIGNUP_DEMO_LIMIT,
  SUBSCRIPTION_POLL_DELAYS_MS,
  SUBSCRIPTION_LONG_VERIFY_THRESHOLD_MS,
  PLAN_TIER_ORDER,
} from '../utils/constants';
import { createApiClient } from '../utils/apiClient';
import { isSubscriptionActive } from '../utils/subscriptionHelpers';
import { trackEvent, TRACKED_EVENTS } from '../utils/analytics';
import { planAmountUsd } from '../data/plans';
import {
  isAuthPopup, authPopupTab, authPopupCheckoutPlan, planEmbedConfig,
} from '../utils/embedOptions';

// ─── Environment constants ────────────────────────────────────────────────────

const isCloudFrontUrl = (() => {
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  const isCloudFrontHost = host.includes('cloudfront.net');
  const envApi = process.env.REACT_APP_API_URL || '';
  const envIndicatesCloudFront = envApi.includes('cloudfront.net') || envApi.includes('execute-api');
  return isCloudFrontHost || (!isLocal) || envIndicatesCloudFront;
})();

const API_BASE_URL = (() => {
  const CLOUDFRONT_URL = 'https://d3g8bs5q7ol0pr.cloudfront.net/api';
  const envApi = process.env.REACT_APP_API_URL;
  if (envApi) return envApi;
  const host = (typeof window !== 'undefined' && window.location?.hostname) || '';
  if (host.includes('cloudfront.net')) return `https://${host}/api`;
  const isLocal = host === 'localhost' || host === '127.0.0.1';
  if (!isLocal && isCloudFrontUrl) return CLOUDFRONT_URL;
  return 'http://localhost:5002';
})();

// The partner site we are embedded on, and where a buyer should land there
// after paying. Configured here rather than on their page so the redirect can
// be turned on without them editing anything; ?thanks= on a plan button still
// overrides it, which is how any future partner would set their own.
const PARTNER_ORIGIN = (process.env.REACT_APP_PARTNER_ORIGIN || '').replace(/\/+$/, '');
const PARTNER_THANKS_PATH = process.env.REACT_APP_PARTNER_THANKS_PATH || '';

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'password';
const isSubscription = AUTH_MODE === 'subscription';
// Must be set via environment variables. See frontend/.env.example.
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_LOGIN_URI = 'https://d2a79jfkoxjkg0.cloudfront.net';

// ─── Utilities ────────────────────────────────────────────────────────────────

export const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Stripe Checkout and the billing portal send `frame-ancestors 'none'`, so navigating
// the current window breaks when the app is embedded in an iframe (e.g. the WordPress
// partner embed). Assigning to window.top.location is allowed cross-origin — only
// *reading* it is blocked — so this escapes the frame when framed and is a plain
// redirect otherwise. Falls back to a new tab if a sandboxed frame denies navigation.
export const navigateTop = (url) => {
  try {
    (window.top || window).location.href = url;
  } catch (_) {
    window.open(url, '_blank', 'noopener');
  }
};

// Because Stripe takes over the whole tab (above), the page it redirects back to
// is the last thing the visitor sees. Defaulting that to our own origin strands
// embedded visitors: the browser partitions storage, so the token they signed in
// with *inside the partner's iframe* does not exist at top level, and they would
// land on a confirmation page that cannot verify anything. Returning them to the
// partner's page instead puts them back beside the iframe that does hold their
// token. `document.referrer` is the parent page's URL when we are framed.
//
// Returns null when we are not embedded, or when the referrer is missing or
// unusable — callers then fall back to our own origin.
export const getEmbedParentUrl = () => {
  try {
    if (window.self === window.top) return null;
    const referrer = document.referrer;
    if (!referrer) return null;
    const parsed = new URL(referrer);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    return parsed.toString();
  } catch (_) {
    return null;
  }
};

// Post-purchase landing page on the partner's own site, from ?thanks= on the
// plan button's iframe URL (see planEmbedConfig).
//
// Checkout runs in a popup, so the partner's page never reloads after a
// purchase — there is no page load for a conversion tag to fire on, and a tag
// on the pricing page would count visitors rather than buyers. Sending the top
// window somewhere purchase-only gives them that page load.
//
// `thanksPath` is a PATH, never a URL, and it resolves against the parent
// page's own origin. Accepting a full URL here would be an open redirect: the
// value arrives on a query string anyone can edit, and it is handed to
// window.top.location.
//
// Returns the base URL with no query of its own, so callers append `purchase=`
// themselves — the popup path percent-encodes a real session id, while the
// redirect path needs Stripe's {CHECKOUT_SESSION_ID} placeholder to survive
// verbatim, and URLSearchParams would encode its braces out of recognition.
//
// Null when we are not embedded, nothing was configured, or the path is not a
// plain same-origin path — callers then keep their existing behaviour.
export const buildPartnerReturnUrl = (thanksPath) => {
  const parent = getEmbedParentUrl();
  if (!parent) return null;
  let parentOrigin;
  try {
    parentOrigin = new URL(parent).origin;
  } catch (_) {
    return null;
  }
  // Fall back to the path configured for our known partner, so their pricing
  // page needs no edit to start sending buyers to their thank-you page. The
  // origin must match: without that check, anyone else embedding us would send
  // their visitors to a path on their own site that does not exist.
  const path = thanksPath
    || ((PARTNER_ORIGIN && parentOrigin === PARTNER_ORIGIN) ? PARTNER_THANKS_PATH : '');
  if (!path) return null;
  // Must start with a single slash: rejects absolute URLs ("https://evil.test")
  // and scheme-relative ones ("//evil.test", "/\evil.test").
  if (!/^\/[^/\\]/.test(path)) return null;
  // Printable ASCII only. Whitespace and control characters are stripped or
  // normalised by browsers before parsing, which is how a guard like this gets
  // walked past. Written as escapes so no literal control byte lands in source.
  if (/[^\x21-\x7e]/.test(path)) return null;
  try {
    const url = new URL(path, parentOrigin);
    // Same-origin-as-parent is not only the open-redirect defence. Measured:
    // assigning window.top.location to any OTHER origin throws SecurityError
    // from a cross-origin frame, so this is also what makes the redirect work.
    if (url.origin !== parentOrigin) return null;
    return url.toString();
  } catch (_) {
    return null;
  }
};

// `&value=…&currency=USD` for a partner return URL, or '' when the plan is
// unknown.
//
// Both sides report the same purchase and Google collapses them on the shared
// transaction id — but when the two reports disagree on value, which number
// survives is not ours to choose. So the amount travels to the partner rather
// than being guessed there, and both reports carry the same one.
//
// The visitor can of course edit it, the same as any thank-you-page total.
// The only thing that buys them is a wrong number in our own ads reporting.
const purchaseValueQuery = (priceId) => {
  const value = planAmountUsd(priceId);
  return value > 0 ? `&value=${value}&currency=USD` : '';
};

// Set just before handing an embedded visitor to Stripe. On their way back the
// app cannot read the parent page's query string (cross-origin), so this marker
// in partitioned localStorage is how the embedded frame knows to re-verify the
// subscription. Stale markers are ignored.
export const CHECKOUT_RETURN_KEY = 'checkoutReturnPending';
export const CHECKOUT_RETURN_MAX_AGE_MS = 30 * 60 * 1000;

// Written by the frame that learns a popup checkout completed; the storage
// event fans the news out to sibling frames (converter, other plan buttons) so
// the whole embedded page refreshes without a reload. postMessage can't do
// this — the popup only holds a reference to its opener, and BroadcastChannel
// doesn't cross the popup/iframe partition boundary in browsers that partition
// third-party storage.
export const SUBSCRIPTION_PING_KEY = 'msgpdfSubscriptionPing';

export const axiosRetry = async (fn, label = 'request', maxAttempts = 3, baseDelayMs = 300) => {
  let attempt = 0;
  let lastError;
  while (attempt < maxAttempts) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      const status = err?.response?.status;
      const code = err?.code || '';
      const shouldRetry = status >= 500 || status === 429 || code === 'ERR_NETWORK' || code === 'ECONNABORTED';
      attempt += 1;
      if (attempt >= maxAttempts || !shouldRetry) break;
      // eslint-disable-next-line no-console
      console.warn(`[axiosRetry] ${label} attempt ${attempt}/${maxAttempts} failed (status=${status} code=${code}), retrying...`);
      await sleep(baseDelayMs * attempt);
    }
  }
  throw lastError;
};

// ─── Context ─────────────────────────────────────────────────────────────────

const AppContext = createContext(null);

export function AppProvider({ children }) {
  // Auth state
  const [token, setToken] = useState('');
  const [authOk, setAuthOk] = useState(false);
  const [email, setEmail] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('pending_payment');
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [pendingSubscriptionEmail, setPendingSubscriptionEmail] = useState('');
  const [infoMessage, setInfoMessage] = useState('');

  // Plan / usage state
  const [planType, setPlanType] = useState('free');
  const [planAmount, setPlanAmount] = useState(null);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState(null);
  const [cardLast4, setCardLast4] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [monthlyConversionsUsed, setMonthlyConversionsUsed] = useState(0);
  const [monthlyConversionLimit, setMonthlyConversionLimit] = useState(SIGNUP_DEMO_LIMIT);
  const [conversionsRemaining, setConversionsRemaining] = useState(SIGNUP_DEMO_LIMIT);
  const [monthlyResetDate, setMonthlyResetDate] = useState(null);

  // App navigation
  const [currentPage, setCurrentPage] = useState('converter');
  const [isVerifyingLong, setIsVerifyingLong] = useState(false);

  // Global dialog / snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  // Auth modal control (opened from many places). The sign-in popup exists for
  // nothing else, so it starts with the modal already up on the requested tab.
  const [authModalOpen, setAuthModalOpen] = useState(isAuthPopup);
  const [authTab, setAuthTab] = useState(isAuthPopup ? authPopupTab : 'signin');
  const [pendingCheckoutPlan, setPendingCheckoutPlan] = useState(null);
  const [planDialogOpen, setPlanDialogOpen] = useState(false);

  // Legacy password-mode state
  const [password, setPassword] = useState('');
  const [requiresAuth, setRequiresAuth] = useState(!isSubscription);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);

  // ─── Scroll refs (lifted so ConversionPage can scroll to pricing) ──────────
  const pricingRef = React.useRef(null);
  const authSectionRef = React.useRef(null);

  const scrollToPricing = useCallback(() => {
    try { pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
  }, []);

  const scrollToAuth = useCallback(() => {
    try {
      if (authSectionRef.current) authSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      else pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {}
  }, []);

  // ─── Auth header helper ───────────────────────────────────────────────────

  const getAuthHeaders = useCallback(() => {
    if (isSubscription) return token ? { Authorization: `Bearer ${token}` } : {};
    return password ? { 'X-App-Password': password, Authorization: `Bearer ${password}` } : {};
  }, [token, password]);

  // ─── API client ───────────────────────────────────────────────────────────
  // Shared axios wrapper. New code should use this; existing direct axios calls
  // remain valid and can migrate incrementally.

  const apiClient = useMemo(
    () => createApiClient({ baseURL: API_BASE_URL, getAuthHeaders }),
    [getAuthHeaders],
  );

  // ─── Subscription status fetch ────────────────────────────────────────────

  const fetchSubscriptionStatus = useCallback(async () => {
    if (!token) return;
    try {
      const res = await apiClient.post('/api/subscription/status', {});
      const data = res?.data || {};
      setSubscriptionRequired(Boolean(data.subscription_required));
      setSubscriptionStatus(data.subscription_status || 'pending_payment');
      setPlanType(data.plan_type || 'free');
      setPlanAmount(data.plan_amount || null);
      setCurrentPeriodEnd(data.current_period_end || null);
      setCardLast4(data.card_last4 || null);
      setCardBrand(data.card_brand || null);
      setMonthlyConversionsUsed(data.monthly_conversions_used || 0);
      setMonthlyConversionLimit(data.monthly_conversion_limit || SIGNUP_DEMO_LIMIT);
      setConversionsRemaining(data.conversions_remaining ?? Math.max(0, (data.monthly_conversion_limit || SIGNUP_DEMO_LIMIT) - (data.monthly_conversions_used || 0)));
      setMonthlyResetDate(data.monthly_reset_date || null);
      if (data.email) {
        setEmail(data.email);
        window.localStorage.setItem('userEmail', data.email);
      }
    } catch (_) {}
  }, [token, apiClient]);

  // ─── Auth init ───────────────────────────────────────────────────────────

  useEffect(() => {
    if (isSubscription) {
      const savedToken = window.localStorage.getItem('jwt') || '';
      if (savedToken) {
        setToken(savedToken);
        setAuthOk(true);
        setSubscriptionRequired(false);
        setPendingSubscriptionEmail('');
      }
      return;
    }
    const saved = window.localStorage.getItem('appPassword') || '';
    if (saved) {
      setPassword(saved);
      checkAuth(saved);
    } else {
      checkAuthRequirement();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep separate same-origin frames in sync. The partner embed runs the
  // sign-in button (?embed=auth) and the converter as two independent iframes,
  // which are two separate documents sharing one localStorage. The `storage`
  // event fires only in the *other* documents, so signing in over there lands
  // here — and logging out over there clears here.
  useEffect(() => {
    if (!isSubscription) return undefined;
    const onStorage = (e) => {
      if (e.key !== 'jwt') return;
      const next = e.newValue || '';
      setToken(next);
      setAuthOk(Boolean(next));
      if (next) {
        setSubscriptionRequired(false);
        setPendingSubscriptionEmail('');
      } else {
        setEmail('');
        setSubscriptionStatus('pending_payment');
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (token) fetchSubscriptionStatus();
  }, [token, fetchSubscriptionStatus]);

  useEffect(() => {
    if (currentPage === 'billing' && token) fetchSubscriptionStatus();
  }, [currentPage, token, fetchSubscriptionStatus]);

  // ─── Logout ───────────────────────────────────────────────────────────────

  const handleLogout = useCallback(() => {
    setToken('');
    setAuthOk(false);
    setSubscriptionRequired(false);
    setSubscriptionStatus('pending_payment');
    setPendingSubscriptionEmail('');
    setInfoMessage('');
    window.localStorage.removeItem('jwt');
  }, []);

  // ─── Legacy password auth ─────────────────────────────────────────────────

  const checkAuthRequirement = useCallback(async () => {
    if (isSubscription) { setRequiresAuth(false); return; }
    if (isCloudFrontUrl) { setRequiresAuth(true); setAuthOk(false); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/auth/check`, {}, {});
      if (res?.data?.auth === 'not-required') { setRequiresAuth(false); setAuthOk(true); }
      else { setRequiresAuth(true); setAuthOk(false); }
    } catch (_) { setRequiresAuth(true); setAuthOk(false); }
  }, []);

  const checkAuth = useCallback(async (pw) => {
    if (!pw) { setAuthOk(false); return false; }
    setIsCheckingAuth(true);
    try {
      if (isCloudFrontUrl) {
        try {
          await axios.get(`${API_BASE_URL}/api/health?auth=${encodeURIComponent(pw)}`);
          setRequiresAuth(true); setAuthOk(true);
          window.localStorage.setItem('appPassword', pw); setPassword(pw);
          return true;
        } catch (_) { setAuthOk(false); return false; }
      } else {
        const headers = { 'X-App-Password': pw, Authorization: `Bearer ${pw}` };
        try {
          const res = await axios.post(`${API_BASE_URL}/auth/check`, {}, { headers });
          if (res?.data?.auth === 'not-required') { setRequiresAuth(false); setAuthOk(true); }
          else { setRequiresAuth(true); setAuthOk(true); }
          window.localStorage.setItem('appPassword', pw); setPassword(pw);
          return true;
        } catch (_) { setAuthOk(false); return false; }
      }
    } finally { setIsCheckingAuth(false); }
  }, []);

  const handleUnlock = useCallback(async () => {
    if (!password.trim()) { setSnackbar({ open: true, message: 'Please enter a password', severity: 'warning' }); return; }
    const ok = await checkAuth(password.trim());
    if (!ok) setSnackbar({ open: true, message: 'Authentication failed. Please check your password.', severity: 'error' });
  }, [password, checkAuth]);

  // ─── Checkout / plan management ───────────────────────────────────────────

  // Single implementation of POST /api/subscription/change; the checkout
  // confirm-dialog and BillingPortal both delegate here.
  const handleChangePlan = useCallback(async (plan) => {
    if (!token) { setInfoMessage('Sign in to manage your subscription.'); setAuthModalOpen(true); setAuthTab('signin'); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/subscription/change`, { plan }, { headers: { Authorization: `Bearer ${token}` } });
      trackEvent(TRACKED_EVENTS.PLAN_CHANGED, { plan });
      setSnackbar({ open: true, message: res?.data?.message || 'Plan updated.', severity: 'success' });
      setSubscriptionRequired(false);
      setSubscriptionStatus(res?.data?.subscription_status || 'active');
      await fetchSubscriptionStatus();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to change plan.', severity: 'error' });
    }
  }, [token, fetchSubscriptionStatus]);

  // Watches a checkout popup from the opener side. Stripe disowns
  // window.opener inside the popup (measured: opener is null once it returns to
  // our origin), so the popup cannot report back itself — but disowning does
  // not invalidate OUR reference to the popup, and once it navigates back to
  // our origin its location becomes readable again. Poll for that moment, read
  // the outcome off the URL, close the popup, and refresh this frame plus its
  // siblings.
  // `priceId` is only used to price the conversion reported from the partner's
  // thank-you page — their tag is static markup and cannot work out what was
  // bought, so the amount has to arrive on the URL.
  const watchCheckoutPopup = useCallback((popupWindow, priceId = '') => {
    const startedAt = Date.now();
    const pollTimeouts = [];
    const timer = setInterval(() => {
      let finished = false;
      try {
        if (popupWindow.closed) {
          // Closed by hand — possibly right after paying, so re-check once.
          finished = true;
          fetchSubscriptionStatus();
        } else {
          // Throws SecurityError while the popup is on checkout.stripe.com;
          // becomes readable when Stripe redirects back to our origin.
          const { pathname, search } = popupWindow.location;
          const returned = (search || '').includes('popup_return=1') || pathname.startsWith('/subscription-confirmed');
          if (returned) {
            finished = true;
            // The success effect in the popup rewrites ?session_id=cs_… to
            // /subscription-confirmed, so either shape means "paid".
            const paid = pathname.startsWith('/subscription-confirmed') || /[?&]session_id=cs_/.test(search || '');
            try { popupWindow.close(); } catch (_) {}
            if (paid) {
              setSnackbar({ open: true, message: 'Payment received — updating your account…', severity: 'success' });
              fetchSubscriptionStatus();
              SUBSCRIPTION_POLL_DELAYS_MS.forEach(delay => pollTimeouts.push(setTimeout(fetchSubscriptionStatus, delay)));
              try { window.localStorage.setItem(SUBSCRIPTION_PING_KEY, String(Date.now())); } catch (_) {}

              // Take the partner's page to their thank-you page, if they
              // configured one. This is the only moment their site sees a page
              // load after a purchase — the popup did all the work and their
              // page never reloaded — so it is where a conversion tag can fire.
              // The session id rides along so the tag can be gated on it and
              // not re-fire when the page is refreshed, bookmarked or shared.
              const thanksBase = buildPartnerReturnUrl(planEmbedConfig.thanksPath);
              if (thanksBase) {
                const sessionId = (/[?&]session_id=(cs_[^&]*)/.exec(search || '') || [])[1] || '';
                const separator = thanksBase.includes('?') ? '&' : '?';
                navigateTop(thanksBase + (sessionId
                  ? `${separator}purchase=${encodeURIComponent(sessionId)}${purchaseValueQuery(priceId)}`
                  : ''));
              }
            }
          }
        }
      } catch (_) { /* still on Stripe */ }
      if (finished || Date.now() - startedAt > CHECKOUT_RETURN_MAX_AGE_MS) clearInterval(timer);
    }, 800);
  }, [fetchSubscriptionStatus]);

  // `opts.popupWindow` — a window already opened during the user's click (see
  // utils/checkoutPopup.js). When present and usable, Stripe loads there instead
  // of taking over the top-level tab, and the success/cancel URLs switch to the
  // popup_return flow below. Every path that does not reach Stripe must close it
  // or the visitor is left with a stray "Opening checkout…" window.
  //
  // A sign-in popup continuing to Stripe passes ITSELF as popupWindow (see the
  // auth-complete effect below), so closing on a dead end would shut the window
  // the visitor is reading. In that case leave it open — the snackbar and the
  // dialog it already renders explain what happened.
  const handleCheckout = useCallback(async (priceId, autoStart = false, forcedEmail = undefined, opts = {}) => {
    const popupWindow = (opts.popupWindow && !opts.popupWindow.closed) ? opts.popupWindow : null;
    const isSelf = popupWindow === window;
    const closePopup = () => { if (isSelf) return; try { popupWindow?.close(); } catch (_) {} };
    const subscriberEmail = forcedEmail || email || pendingSubscriptionEmail || window.localStorage.getItem('userEmail') || '';

    if (!authOk && !token && !autoStart) {
      closePopup();
      setPendingCheckoutPlan(priceId);
      setInfoMessage('Create an account or sign in to continue to checkout.');
      scrollToAuth();
      setAuthModalOpen(true);
      setAuthTab('signin');
      return;
    }

    if (!token && !subscriberEmail) {
      closePopup();
      setSnackbar({ open: true, message: 'Please create an account or log in first to subscribe.', severity: 'warning' });
      return;
    }

    // Already subscribed — show plan-change confirmation
    if (authOk && isSubscriptionActive(subscriptionStatus)) {
      closePopup();
      const { planCards } = await import('../data/plans');
      const isYearly = priceId.includes('_yearly');
      const basePlanId = priceId.replace('_yearly', '');
      const planDetails = planCards.find(p => p.id === basePlanId);
      if (!planDetails) { setSnackbar({ open: true, message: 'Invalid plan selected.', severity: 'error' }); return; }

      const newPrice = isYearly ? planDetails.yearlyPrice : planDetails.price;
      const newPeriod = isYearly ? (planDetails.yearlyPeriod || '/yr') : (planDetails.period || '/mo');

      const isDowngrade = (PLAN_TIER_ORDER[basePlanId] || 0) < (PLAN_TIER_ORDER[planType] || 0);

      setConfirmDialog({
        open: true,
        title: isDowngrade ? 'Confirm Plan Downgrade' : 'Confirm Plan Change',
        message: `Switch to ${planDetails.name} (${newPrice}${newPeriod})? ${isDowngrade ? 'Your plan will downgrade at end of billing cycle.' : 'Prorated charges will be calculated by Stripe.'}`,
        onConfirm: () => handleChangePlan(priceId)
      });
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      // Popup: return to our own origin *in the popup*, flagged popup_return so
      // that page reports back to the opener frame and closes itself.
      // Redirect while embedded: come back to the partner's page, where the
      // frame holding this visitor's token lives.
      // Redirect standalone: our own origin, where ?session_id drives the
      // confirmation screen.
      const parentUrl = getEmbedParentUrl();
      // Blocked popup or mobile: Stripe returns the whole tab. Land it on the
      // partner's thank-you page when they have one, so that path produces the
      // same page load their conversion tag needs. The placeholder is passed
      // through unencoded on purpose — Stripe only substitutes a literal
      // {CHECKOUT_SESSION_ID}, and URLSearchParams would escape the braces.
      const thanksBase = buildPartnerReturnUrl(planEmbedConfig.thanksPath);
      const thanksRedirect = thanksBase
        ? `${thanksBase}${thanksBase.includes('?') ? '&' : '?'}purchase={CHECKOUT_SESSION_ID}${purchaseValueQuery(priceId)}`
        : null;
      const payload = popupWindow
        ? {
          priceId,
          // `plan` rides back so the conversion can be reported with what the
          // sale was actually worth. Without it every purchase books the
          // action's $1 default and value-based bidding sees a $49 Team plan
          // and a $7.99 Starter as the same outcome.
          successUrl: `${window.location.origin}/?session_id={CHECKOUT_SESSION_ID}&popup_return=1&plan=${encodeURIComponent(priceId)}`,
          cancelUrl: `${window.location.origin}/?popup_return=1`,
        }
        : {
          priceId,
          successUrl: thanksRedirect || parentUrl
            || `${window.location.origin}?session_id={CHECKOUT_SESSION_ID}&plan=${encodeURIComponent(priceId)}`,
          // Cancel is not a purchase, so it must never reach the thank-you page.
          cancelUrl: parentUrl || window.location.origin,
        };
      if (subscriberEmail) payload.email = subscriberEmail;
      const res = await axios.post(`${API_BASE_URL}/api/create-checkout-session`, payload, { headers });
      if (res.data?.url) {
        trackEvent(TRACKED_EVENTS.CHECKOUT_STARTED, { plan: priceId });
        if (popupWindow) {
          popupWindow.location.href = res.data.url;
          watchCheckoutPopup(popupWindow, priceId);
        } else {
          // The partner's URL carries no session_id we can read, so leave a
          // marker for the returning frame to re-verify against.
          if (parentUrl) {
            try { window.localStorage.setItem(CHECKOUT_RETURN_KEY, String(Date.now())); } catch (_) {}
          }
          navigateTop(res.data.url);
        }
      } else {
        closePopup();
        setSnackbar({ open: true, message: 'Failed to start checkout.', severity: 'error' });
      }
    } catch (err) {
      closePopup();
      setSnackbar({ open: true, message: 'Error starting checkout: ' + (err?.response?.data?.error || err.message), severity: 'error' });
    }
  }, [authOk, token, email, pendingSubscriptionEmail, subscriptionStatus, planType, scrollToAuth, handleChangePlan, watchCheckoutPopup]);

  const handleCancelSubscription = useCallback(async () => {
    if (!token) { setInfoMessage('Sign in to manage your subscription.'); setAuthModalOpen(true); setAuthTab('signin'); return; }
    try {
      const res = await axios.post(`${API_BASE_URL}/api/subscription/cancel`, {}, { headers: { Authorization: `Bearer ${token}` } });
      trackEvent(TRACKED_EVENTS.SUBSCRIPTION_CANCELED);
      setSnackbar({ open: true, message: res?.data?.message || 'Subscription canceled.', severity: 'success' });
      setSubscriptionRequired(true);
      setSubscriptionStatus('canceled');
      setPlanType('free');
      await fetchSubscriptionStatus();
    } catch (err) {
      setSnackbar({ open: true, message: err?.response?.data?.error || 'Failed to cancel subscription.', severity: 'error' });
    }
  }, [token, fetchSubscriptionStatus]);

  const handleManageAccount = useCallback(async () => {
    if (!token) { setInfoMessage('Sign in to manage your subscription.'); scrollToAuth(); setAuthModalOpen(true); setAuthTab('signin'); return; }
    try {
      // Same reasoning as checkout: return an embedded visitor to the partner's
      // page rather than stranding them on our origin.
      const returnUrl = getEmbedParentUrl() || window.location.origin;
      const res = await axios.post(`${API_BASE_URL}/api/manage/portal`, { returnUrl }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.url) { navigateTop(res.data.url); return; }
      setSnackbar({ open: true, message: 'Could not load billing portal.', severity: 'error' });
    } catch (err) {
      const apiMessage = err?.response?.data?.error || err.message;
      if (err?.response?.status === 400 && apiMessage?.toLowerCase().includes('no subscription')) {
        setInfoMessage('No subscription on file. Please start a plan to enable the billing portal.');
        scrollToAuth();
        pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      setSnackbar({ open: true, message: 'Error opening billing portal: ' + apiMessage, severity: 'error' });
    }
  }, [token, scrollToAuth]);

  // ─── Checkout resume ──────────────────────────────────────────────────────

  // A standalone plan button (?embed=plan) with no converter frame beside it
  // sends signed-out visitors here as /?checkout=<planId>. Pick the purchase
  // back up: handleCheckout stashes it as pendingCheckoutPlan and the auth
  // modal resumes it once they sign in.
  useEffect(() => {
    if (typeof window === 'undefined' || !isSubscription) return;
    const params = new URLSearchParams(window.location.search || '');
    const requestedPlan = params.get('checkout');
    if (!requestedPlan) return;
    params.delete('checkout');
    window.history.replaceState(
      {}, '',
      `${window.location.origin}${window.location.pathname}${params.toString() ? '?' + params.toString() : ''}`,
    );
    handleCheckout(requestedPlan);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Embedded visitor returning from Stripe. There is no readable session_id on
  // the partner's URL, so the marker set before checkout is the signal. Stripe's
  // webhook may still be in flight, hence the same poll schedule the standalone
  // confirmation screen uses.
  useEffect(() => {
    if (!isSubscription || !token) return undefined;
    let marker = null;
    try { marker = window.localStorage.getItem(CHECKOUT_RETURN_KEY); } catch (_) { return undefined; }
    if (!marker) return undefined;
    try { window.localStorage.removeItem(CHECKOUT_RETURN_KEY); } catch (_) {}

    const startedAt = parseInt(marker, 10);
    if (!Number.isFinite(startedAt) || Date.now() - startedAt > CHECKOUT_RETURN_MAX_AGE_MS) return undefined;

    fetchSubscriptionStatus();
    const timeouts = SUBSCRIPTION_POLL_DELAYS_MS.map(delay => setTimeout(fetchSubscriptionStatus, delay));
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [token, fetchSubscriptionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Popup side of popup checkout: Stripe returned this window to our origin
  // with ?popup_return=1 (set only by the popup branch of handleCheckout).
  // Report the outcome to the frame that opened us, then close so the visitor
  // is back on the page they never left. Gated on our own param so an ordinary
  // tab that happens to have an opener is never closed.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.opener) return undefined;
    const params = new URLSearchParams(window.location.search || '');
    if (params.get('popup_return') !== '1') return undefined;
    const paid = (params.get('session_id') || '').startsWith('cs_');
    try { window.opener.postMessage({ type: 'msgpdf:checkout-complete', paid }, window.location.origin); } catch (_) {}
    // Give a paid confirmation a moment on screen; a cancel just closes.
    const t = setTimeout(() => { try { window.close(); } catch (_) {} }, paid ? 1500 : 0);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Popup side of popup sign-in: we are the window opened by a standalone
  // ?embed=auth or ?embed=plan button that had no converter frame to hand off
  // to. Once a session exists, give it to the frame that opened us and close.
  //
  // The token has to travel by postMessage rather than localStorage: browsers
  // partition third-party storage, so this window (first-party on our origin)
  // and the button iframe (partitioned under the partner's site) do not share
  // one. Gated on our own param so an ordinary tab is never closed.
  //
  // With ?checkout=<planId> the visitor was buying a plan, not just signing in,
  // so this window carries on to Stripe instead of closing. It has to be THIS
  // window: the click that opened it is long spent, and a fresh window.open for
  // Stripe would be blocked. Hand the token over first either way — that is what
  // signs the partner page's other frames in.
  const checkoutContinued = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !isAuthPopup || !window.opener || !token) return undefined;
    const who = email || window.localStorage.getItem('userEmail') || '';
    try {
      window.opener.postMessage({ type: 'msgpdf:auth-complete', token, email: who }, window.location.origin);
    } catch (_) {}

    if (authPopupCheckoutPlan) {
      // This effect reruns as `email` arrives from the status fetch; only the
      // first pass may start a checkout session. The auth dialog is left open
      // deliberately — on success the navigation to Stripe discards it, and on
      // failure it beats leaving the visitor a blank popup with a snackbar.
      if (checkoutContinued.current) return undefined;
      checkoutContinued.current = true;
      handleCheckout(authPopupCheckoutPlan, false, undefined, { popupWindow: window });
      return undefined;
    }

    // Long enough for the modal's success state to register, short enough not
    // to feel like a stall.
    const t = setTimeout(() => { try { window.close(); } catch (_) {} }, 600);
    return () => clearTimeout(t);
  }, [token, email]); // eslint-disable-line react-hooks/exhaustive-deps

  // Opener side of popup sign-in: adopt the session the popup just handed us.
  // Writing it to localStorage also fires the `storage` listener above in any
  // sibling frame sharing this partition, so a converter embed on the same page
  // updates without a reload.
  useEffect(() => {
    if (typeof window === 'undefined' || !isSubscription) return undefined;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'msgpdf:auth-complete') return;
      const nextToken = event.data.token || '';
      if (!nextToken) return;
      const nextEmail = (event.data.email || '').toLowerCase();
      setToken(nextToken);
      setAuthOk(true);
      setSubscriptionRequired(false);
      try {
        window.localStorage.setItem('jwt', nextToken);
        if (nextEmail) window.localStorage.setItem('userEmail', nextEmail);
      } catch (_) {}
      if (nextEmail) setEmail(nextEmail);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Opener side: the popup reported back. Re-verify on the same schedule as the
  // redirect flow — Stripe's webhook may still be in flight — and ping sibling
  // frames so the converter's account panel updates too.
  useEffect(() => {
    if (typeof window === 'undefined' || !isSubscription) return undefined;
    const timeouts = [];
    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== 'msgpdf:checkout-complete' || !event.data.paid) return;
      setSnackbar({ open: true, message: 'Payment received — updating your account…', severity: 'success' });
      fetchSubscriptionStatus();
      SUBSCRIPTION_POLL_DELAYS_MS.forEach(delay => timeouts.push(setTimeout(fetchSubscriptionStatus, delay)));
      try { window.localStorage.setItem(SUBSCRIPTION_PING_KEY, String(Date.now())); } catch (_) {}
    };
    window.addEventListener('message', onMessage);
    return () => { window.removeEventListener('message', onMessage); timeouts.forEach(t => clearTimeout(t)); };
  }, [fetchSubscriptionStatus]);

  // Sibling side of the ping: another frame learned of a completed payment.
  useEffect(() => {
    if (typeof window === 'undefined' || !isSubscription) return undefined;
    const timeouts = [];
    const onStorage = (event) => {
      if (event.key !== SUBSCRIPTION_PING_KEY || !event.newValue) return;
      fetchSubscriptionStatus();
      SUBSCRIPTION_POLL_DELAYS_MS.forEach(delay => timeouts.push(setTimeout(fetchSubscriptionStatus, delay)));
    };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('storage', onStorage); timeouts.forEach(t => clearTimeout(t)); };
  }, [fetchSubscriptionStatus]);

  // ─── Subscription success polling ─────────────────────────────────────────

  // Showing the confirmation screen and reporting a sale are different things,
  // and this effect used to conflate them.
  //
  // It reruns whenever the token settles, and the rewrite below strips
  // session_id — so the second run still matched on the pathname alone and
  // fired the event again, for one purchase. A bare visit to
  // /subscription-confirmed, from a bookmark or a shared link, matched too.
  // Neither is a sale. The conversion now needs a real Stripe session id, and
  // fires at most once.
  const conversionReported = React.useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search || '');
    const sessionId = params.get('session_id');
    const isConfirmationPath = window.location.pathname === '/subscription-confirmed';
    const paidNow = Boolean(sessionId && sessionId.startsWith('cs_'));

    if (paidNow || isConfirmationPath) {
      setCurrentPage('subscription-confirmed');

      if (paidNow && !conversionReported.current) {
        conversionReported.current = true;
        // The session id is also what lets Google collapse this with the same
        // purchase reported from the partner's thank-you page — both sides have
        // to send it or there is nothing to match on.
        //
        // The value comes off ?plan=, put there by handleCheckout's success
        // URL, because Stripe's return carries no hint of what was bought.
        // Omitted rather than sent as 0 when the plan is missing or unknown: a
        // conversion with no value falls back to the action's default, while an
        // explicit 0 teaches value bidding that the sale was worthless.
        const amount = planAmountUsd(params.get('plan') || '');
        trackEvent(TRACKED_EVENTS.SUBSCRIBE_COMPLETED, {
          transaction_id: sessionId,
          ...(amount > 0 ? { value: amount, currency: 'USD' } : {}),
        });
      }

      // Tidying the URL is for a window the visitor keeps looking at. In a
      // checkout popup it is actively harmful: the opener reads session_id off
      // THIS window's URL (watchCheckoutPopup polls every 800ms), so rewriting
      // it here is a race — and one the opener loses whenever the bundle is
      // warm, which is exactly the case after a sign-in popup continued to
      // Stripe. The opener still detects the return via the path, still
      // navigates the partner's page, but with no ?purchase= on it, so their
      // conversion tag finds nothing and exits silently: a paid sale, a clean
      // thank-you page, and zero conversions recorded.
      //
      // The opener closes this window seconds later, so its URL never needs
      // cleaning. Leaving the id in place makes the handoff deterministic at
      // any boot speed, including a backgrounded opener whose timer is
      // throttled.
      if (sessionId && params.get('popup_return') !== '1') {
        params.delete('session_id');
        params.delete('plan');
        const clean = `${window.location.origin}/subscription-confirmed${params.toString() ? '?' + params.toString() : ''}`;
        window.history.replaceState({}, '', clean);
      }

      if (token) {
        fetchSubscriptionStatus();
        setIsVerifyingLong(false);
        const timeouts = SUBSCRIPTION_POLL_DELAYS_MS.map(delay => setTimeout(() => {
          fetchSubscriptionStatus();
          if (delay >= SUBSCRIPTION_LONG_VERIFY_THRESHOLD_MS) setIsVerifyingLong(true);
        }, delay));
        return () => timeouts.forEach(t => clearTimeout(t));
      }
    }
  }, [token, fetchSubscriptionStatus]);

  // Memoized so provider re-renders only propagate to consumers when
  // something in the context actually changed.
  const value = useMemo(() => ({
    // Constants
    API_BASE_URL, isCloudFrontUrl, AUTH_MODE, isSubscription,
    GOOGLE_CLIENT_ID, GOOGLE_LOGIN_URI,
    GUEST_DEMO_LIMIT, SIGNUP_DEMO_LIMIT,
    // Auth state
    token, setToken, authOk, setAuthOk, email, setEmail,
    subscriptionStatus, setSubscriptionStatus,
    subscriptionRequired, setSubscriptionRequired,
    pendingSubscriptionEmail, setPendingSubscriptionEmail,
    infoMessage, setInfoMessage,
    // Plan state
    planType, setPlanType, planAmount, currentPeriodEnd, cardLast4, cardBrand,
    monthlyConversionsUsed, monthlyConversionLimit, conversionsRemaining, monthlyResetDate,
    // Navigation
    currentPage, setCurrentPage, isVerifyingLong,
    // Dialogs
    snackbar, setSnackbar, confirmDialog, setConfirmDialog,
    authModalOpen, setAuthModalOpen, authTab, setAuthTab,
    pendingCheckoutPlan, setPendingCheckoutPlan,
    planDialogOpen, setPlanDialogOpen,
    // Legacy password mode
    password, setPassword, requiresAuth, isCheckingAuth,
    // Refs
    pricingRef, authSectionRef,
    // API
    apiClient,
    // Handlers
    scrollToPricing, scrollToAuth, getAuthHeaders,
    fetchSubscriptionStatus, handleLogout,
    handleCheckout, handleChangePlan, handleCancelSubscription, handleManageAccount,
    watchCheckoutPopup,
    checkAuth, checkAuthRequirement, handleUnlock,
  }), [
    token, authOk, email,
    subscriptionStatus, subscriptionRequired, pendingSubscriptionEmail, infoMessage,
    planType, planAmount, currentPeriodEnd, cardLast4, cardBrand,
    monthlyConversionsUsed, monthlyConversionLimit, conversionsRemaining, monthlyResetDate,
    currentPage, isVerifyingLong,
    snackbar, confirmDialog,
    authModalOpen, authTab, pendingCheckoutPlan, planDialogOpen,
    password, requiresAuth, isCheckingAuth,
    apiClient,
    scrollToPricing, scrollToAuth, getAuthHeaders,
    fetchSubscriptionStatus, handleLogout,
    handleCheckout, handleChangePlan, handleCancelSubscription, handleManageAccount,
    watchCheckoutPopup,
    checkAuth, checkAuthRequirement, handleUnlock,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used inside AppProvider');
  return ctx;
}
